import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { digitsOnly } from '../../utils/phone';
import { InlineCreateClientForm } from './InlineCreateClientForm';

/**
 * ClientPicker — admin twin (mobile twin lives at
 * crewbit-mobile/components/common/ClientPicker.tsx).
 *
 * Fuzzy search across `clients` rows in the caller's business: substring
 * match on `name + ' ' + phone` (phone digit-stripped before compare).
 *
 * Two visual states:
 *   - value === null  → search input + dropdown
 *   - value !== null  → removable chip "{name} ×"
 *
 * `allowCreate={true}` (default) appends "+ Create new client {query}" as
 * the last dropdown row when there are no exact matches; tap mounts
 * InlineCreateClientForm inline. The form's onCreated bubbles a full
 * ClientSelection back through onChange — same shape as picking an
 * existing row, so consumers don't branch on create-vs-pick.
 *
 * Q5 Phase 1 (Session 76).
 */

export interface ClientSelection {
  clientId: string;
  name: string;
  phone: string | null;
  email: string | null;
}

export interface ClientPickerProps {
  value: string | null;
  onChange: (selection: ClientSelection | null) => void;
  allowCreate?: boolean;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}

interface ClientRow {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

const MAX_DROPDOWN = 8;

const WRAPPER: React.CSSProperties = {
  position: 'relative',
  width: '100%',
};

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
};

const INPUT_ERROR: React.CSSProperties = {
  borderColor: palette.red,
};

const INPUT_DISABLED: React.CSSProperties = {
  background: palette.g50,
  color: palette.g400,
  cursor: 'not-allowed',
};

const CHIP: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 8px 6px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.accent}`,
  background: palette.accentWash,
  color: palette.accentDeep,
  fontSize: 13.5,
  fontWeight: 500,
};

const CHIP_X: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  borderRadius: 9,
  border: 'none',
  background: 'transparent',
  color: palette.accentDeep,
  cursor: 'pointer',
};

const DROPDOWN: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
  maxHeight: 320,
  overflowY: 'auto',
  zIndex: 30,
};

const ROW: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '8px 12px',
  border: 'none',
  background: 'transparent',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  borderBottom: `1px solid ${palette.g100}`,
};

const ROW_NAME: React.CSSProperties = {
  fontSize: 13.5,
  fontWeight: 500,
  color: palette.g800,
};

const ROW_META: React.CSSProperties = {
  fontSize: 12,
  color: palette.g500,
};

const CREATE_ROW: React.CSSProperties = {
  ...ROW,
  color: palette.accentDeep,
  fontWeight: 500,
};

const EMPTY_ROW: React.CSSProperties = {
  padding: '12px',
  fontSize: 13,
  color: palette.g500,
  textAlign: 'center' as const,
};

const ERROR_TEXT: React.CSSProperties = {
  fontSize: 12,
  color: palette.red,
  marginTop: 4,
};

function matches(row: ClientRow, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = `${row.name} ${row.phone ?? ''}`.toLowerCase();
  if (haystack.includes(q)) return true;
  // Phone digit match (strip non-digits from query, compare against stored digits).
  const qDigits = q.replace(/\D/g, '');
  if (qDigits.length >= 3 && row.phone) {
    if (digitsOnly(row.phone).includes(qDigits)) return true;
  }
  return false;
}

export function ClientPicker({
  value,
  onChange,
  allowCreate = true,
  error,
  disabled = false,
  autoFocus = false,
  placeholder = 'Search clients…',
}: ClientPickerProps) {
  const businessId = useAuthStore((s) => s.business?.id);

  const [allClients, setAllClients] = useState<ClientRow[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fetch the tenant's clients once on mount + on businessId change.
  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('clients')
        .select('id, name, phone, email')
        .eq('business_id', businessId)
        .order('name', { ascending: true });
      if (cancelled) return;
      setAllClients((data ?? []) as ClientRow[]);
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  // When value points at a row not yet in the loaded list (deep-link edit
  // or pre-populated form), one-shot fetch and append to allClients so the
  // chip resolves derivationally — no separate chip state needed.
  useEffect(() => {
    if (!value) return;
    if (allClients.some((c) => c.id === value)) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('clients')
        .select('id, name, phone, email')
        .eq('id', value)
        .maybeSingle();
      if (cancelled || !data) return;
      setAllClients((prev) =>
        prev.some((c) => c.id === (data as ClientRow).id) ? prev : [...prev, data as ClientRow],
      );
    })();
    return () => { cancelled = true; };
  }, [value, allClients]);

  const chipRow = useMemo<ClientRow | null>(
    () => (value ? allClients.find((c) => c.id === value) ?? null : null),
    [value, allClients],
  );

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = useMemo(() => {
    return allClients.filter((c) => matches(c, query)).slice(0, MAX_DROPDOWN);
  }, [allClients, query]);

  const handlePick = useCallback((row: ClientRow) => {
    onChange({
      clientId: row.id,
      name: row.name,
      phone: row.phone,
      email: row.email,
    });
    setQuery('');
    setOpen(false);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery('');
  }, [onChange]);

  const handleCreated = useCallback((sel: ClientSelection) => {
    onChange(sel);
    setQuery('');
    setOpen(false);
    setCreating(false);
    // Append to local list so the next search hits cache.
    setAllClients((prev) => {
      if (prev.some((c) => c.id === sel.clientId)) return prev;
      return [
        ...prev,
        { id: sel.clientId, name: sel.name, phone: sel.phone, email: sel.email },
      ].sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [onChange]);

  // Chip mode.
  if (value && chipRow) {
    return (
      <div style={WRAPPER}>
        <span style={CHIP}>
          {chipRow.name}
          {!disabled && (
            <button
              type="button"
              aria-label="Clear client"
              style={CHIP_X}
              onClick={handleClear}
            >
              <X size={12} />
            </button>
          )}
        </span>
        {error && <div style={ERROR_TEXT}>{error}</div>}
      </div>
    );
  }

  return (
    <div style={WRAPPER} ref={wrapperRef}>
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        style={{
          ...INPUT,
          ...(error ? INPUT_ERROR : null),
          ...(disabled ? INPUT_DISABLED : null),
        }}
      />

      {open && !creating && (
        <div style={DROPDOWN}>
          {filtered.length === 0 && !allowCreate && (
            <div style={EMPTY_ROW}>No matches.</div>
          )}
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid="client-picker-row"
              style={ROW}
              onClick={() => handlePick(c)}
            >
              <div style={ROW_NAME}>{c.name}</div>
              {(c.phone || c.email) && (
                <div style={ROW_META}>
                  {c.phone}
                  {c.phone && c.email ? ' · ' : ''}
                  {c.email}
                </div>
              )}
            </button>
          ))}
          {allowCreate && (
            <button
              type="button"
              data-testid="client-picker-create"
              style={CREATE_ROW}
              onClick={() => setCreating(true)}
            >
              + Create new client{query ? ` "${query}"` : ''}
            </button>
          )}
        </div>
      )}

      {creating && (
        <div style={{ marginTop: 8 }}>
          <InlineCreateClientForm
            initialName={query}
            onCreated={handleCreated}
            onCancel={() => setCreating(false)}
          />
        </div>
      )}

      {error && <div style={ERROR_TEXT}>{error}</div>}
    </div>
  );
}
